/**
 * Live updates: the server says what changed, and the screens showing it refetch.
 *
 * One WebSocket per signed-in app. It never carries data — an event names a
 * resource (`messaging.own_message`), an action and an id — so what a person
 * sees is still exactly what the guarded endpoints return them. This module only
 * decides *which cached queries are now out of date*, from a table each app
 * declares, and hands them to TanStack Query, which refetches the ones on screen
 * and marks the rest stale.
 *
 * That table is the one place an app says how its screens relate to the
 * server's resources. A resource it does not list changes nothing on screen,
 * which is the safe failure: a stale view until the next navigation, never a
 * request the page did not ask for.
 *
 * Reconnecting is the part that has to be right:
 *
 * - The socket opens with a single-use ticket from `POST /realtime/ticket`, so a
 *   reconnect always asks for a new one — and asking needs a live session, so a
 *   signed-out account simply stops reconnecting.
 * - The server ends a socket after half an hour (code 4000) and refuses a spent
 *   ticket (4401). Both mean "reconnect now", not "something is wrong".
 * - Anything else is backed off, 1s doubling to 30s, with jitter so a server
 *   restart is not answered by every tab at the same instant.
 * - Events published while the socket was down are gone. So after any
 *   *re*connect, and on a `resync`, every query is invalidated once.
 *
 * Beside changes, the server may send a **signal**: something true only for a
 * moment — somebody typing — that no query holds and nothing should refetch
 * for. A signal is any frame whose `type` is not one of the above. It is handed
 * to whoever listens (`useRealtimeSignal`) and kept nowhere; one missed while
 * the socket was down is simply gone, as it would be on screen anyway.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';

import { useSession } from '../auth/session';
import { api } from '../lib/api-client';
import { apiBaseUrl } from '../config/app-config';

/** One change, as the server announces it. */
export interface RealtimeChange {
  resource: string;
  action: string;
  id: string | null;
}

/**
 * How a resource maps onto an app's cached queries.
 *
 * `resource` is exact (`support.ticket`) or a prefix ending in a dot
 * (`messaging.`). `keys` are query-key prefixes: `['messages']` covers every
 * query whose key starts with it. `session` re-reads `/auth/me`, for a change
 * that alters what the person may see — a service switched on or off.
 */
export interface RealtimeRoute {
  resource: string;
  keys?: readonly QueryKey[];
  session?: boolean;
}

export interface RealtimeRoutes {
  routes: readonly RealtimeRoute[];
  /** Invalidated on every change: summaries that count across resources. */
  always?: readonly QueryKey[];
}

/** Something true only now, as the server says it: `type` and its own fields. */
export interface RealtimeSignal {
  type: string;
  [field: string]: unknown;
}

type SignalListener = (signal: RealtimeSignal) => void;
const signalListeners = new Set<SignalListener>();

/** The frames that are the socket's own business, never a signal. */
const OWN_TYPES = new Set(['ready', 'changed', 'resync', 'ping']);

/** Hand a frame to the signal listeners, when it is a signal. Exported for tests. */
export function dispatchSignal(message: unknown): boolean {
  if (typeof message !== 'object' || message === null) return false;
  const type = (message as { type?: unknown }).type;
  if (typeof type !== 'string' || OWN_TYPES.has(type)) return false;
  for (const listener of [...signalListeners]) listener(message as RealtimeSignal);
  return true;
}

/** Listen for one kind of signal outside React. Returns how to stop. */
export function onRealtimeSignal(type: string, listener: SignalListener): () => void {
  const filtered: SignalListener = (signal) => {
    if (signal.type === type) listener(signal);
  };
  signalListeners.add(filtered);
  return () => signalListeners.delete(filtered);
}

/**
 * Listen for one kind of signal while a component is mounted. The handler may
 * change on every render; the subscription does not.
 */
export function useRealtimeSignal(type: string, handler: SignalListener): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(() => onRealtimeSignal(type, (signal) => latest.current(signal)), [type]);
}

const CLOSE_RENEW = 4000;
const CLOSE_TICKET_REFUSED = 4401;
const MAX_DELAY_MS = 30_000;

/** Whether a route covers a resource: an exact name, or a prefix ending in a dot. */
export function routeMatches(route: RealtimeRoute, resource: string): boolean {
  return route.resource.endsWith('.') ? resource.startsWith(route.resource) : route.resource === resource;
}

/** The query keys a batch of changes makes stale, each once, and whether the session is. */
export function affectedBy(
  changes: readonly RealtimeChange[],
  table: RealtimeRoutes,
): { keys: QueryKey[]; session: boolean } {
  const keys = new Map<string, QueryKey>();
  let session = false;
  const add = (key: QueryKey) => keys.set(JSON.stringify(key), key);
  for (const change of changes) {
    for (const route of table.routes) {
      if (!routeMatches(route, change.resource)) continue;
      route.keys?.forEach(add);
      if (route.session) session = true;
    }
  }
  if (changes.length > 0) table.always?.forEach(add);
  return { keys: [...keys.values()], session };
}

/**
 * The socket's address, from the API base the app already uses.
 *
 * The base is usually relative (`/api`), so it is resolved against the page:
 * a socket speaks `ws:`/`wss:`, which follows from the page's `http:`/`https:`.
 */
export function realtimeUrl(base: string, page: string, ticket: string): string {
  const url = new URL(`${base.replace(/\/$/, '')}/v1/realtime`, page);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.search = new URLSearchParams({ ticket }).toString();
  return url.toString();
}

/** The next wait before reconnecting, doubled from the last, with jitter. */
export function nextDelay(previous: number, random: () => number = Math.random): number {
  const doubled = Math.min(MAX_DELAY_MS, previous === 0 ? 1_000 : previous * 2);
  // ±20%, so a restart is not answered by every open tab at the same instant.
  return Math.round(doubled * (0.8 + random() * 0.4));
}

interface Connection {
  close: () => void;
}

function connect(queryClient: QueryClient, table: RealtimeRoutes): Connection {
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let delay = 0;
  let stopped = false;
  let everConnected = false;

  const apply = (changes: RealtimeChange[]) => {
    const { keys, session } = affectedBy(changes, table);
    for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    // A failure is a session that has since ended — the change announced may
    // be its own ending — and the 401 handler has already signed out with it.
    if (session) void useSession.getState().refreshSession().catch(() => undefined);
  };

  const schedule = (wait: number) => {
    if (stopped) return;
    timer = setTimeout(() => void open(), wait);
  };

  const open = async () => {
    if (stopped) return;
    let ticket: string;
    try {
      ticket = (await api.post<{ ticket: string }>('/realtime/ticket')).ticket;
    } catch {
      // No session (the api client has already cleared it, which stops this),
      // or no Redis on the server: try again later, quietly.
      delay = nextDelay(delay);
      schedule(delay);
      return;
    }
    if (stopped) return;

    const ws = new WebSocket(realtimeUrl(apiBaseUrl(), window.location.href, ticket));
    socket = ws;
    ws.onmessage = (event) => {
      let message: { type?: unknown; changes?: RealtimeChange[] };
      try {
        message = JSON.parse(String(event.data));
      } catch {
        return;
      }
      if (message.type === 'ready') {
        // Whatever changed while there was no socket was never heard.
        if (everConnected) void queryClient.invalidateQueries();
        everConnected = true;
        delay = 0;
      } else if (message.type === 'changed' && message.changes) {
        apply(message.changes);
      } else if (message.type === 'resync') {
        void queryClient.invalidateQueries();
      } else {
        dispatchSignal(message);
      }
    };
    ws.onclose = (event) => {
      if (socket === ws) socket = null;
      if (stopped) return;
      if (event.code === CLOSE_RENEW || event.code === CLOSE_TICKET_REFUSED) {
        schedule(0);
        return;
      }
      delay = nextDelay(delay);
      schedule(delay);
    };
  };

  // A tab brought back after its socket dropped reconnects now, not after the
  // rest of a thirty-second back-off.
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || socket || stopped) return;
    if (timer) clearTimeout(timer);
    delay = 0;
    void open();
  };
  document.addEventListener('visibilitychange', onVisible);

  void open();

  return {
    close: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      socket?.close(1000);
      socket = null;
    },
  };
}

/**
 * Keeps the app's cached queries current while somebody is signed in.
 *
 * Mounted once, inside the `QueryClientProvider`. Opens the socket on sign-in
 * and closes it on sign-out; renders its children unchanged.
 */
export function RealtimeProvider({ table, children }: { table: RealtimeRoutes; children: ReactNode }) {
  const queryClient = useQueryClient();
  const status = useSession((state) => state.status);

  useEffect(() => {
    if (status !== 'authenticated' || typeof WebSocket === 'undefined') return;
    const connection = connect(queryClient, table);
    return () => connection.close();
  }, [status, queryClient, table]);

  return <>{children}</>;
}
